import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import PhotoAlbumBullet from 'dpl/components/PhotoAlbumBullets/PhotoAlbumBullet';
import { makeSequence } from 'dpl/shared/utils';

function getBulletScales({ currentIndex, maxIndex, maxBulletsPadding }) {
  // Calculate the initial start/end indices
  let start = currentIndex - maxBulletsPadding;
  let end = currentIndex + maxBulletsPadding;

  // Adjust the start and end to ensure the indices stay within bounds
  if (start < 0) {
    // If start is negative, shift the range to the right
    end += Math.abs(start);
    start = 0;
  }

  if (end > maxIndex) {
    // If end is greater than maxIndex, shift the range to the left
    start -= end - maxIndex;
    end = maxIndex;
  }

  // Ensure start/end indices are still within bounds
  start = Math.max(start, 0);
  end = Math.min(end, maxIndex);

  const isStartHidden = end === maxIndex && maxIndex > end - start;
  const isEndHidden = start === 0 && maxIndex > end - start;

  return makeSequence(maxIndex + 1).map(i => {
    if (i < start || i > end) {
      return 0;
    }

    if (maxIndex === end - start) {
      return 1;
    }

    const delta = Math.abs(currentIndex - i);

    if ((i === end && isEndHidden) || (i === start && isStartHidden)) {
      return 0.67;
    }

    if (
      (isEndHidden && i < 3) ||
      (isStartHidden && maxIndex - i < 3) ||
      (!isStartHidden && !isEndHidden && delta < 2)
    ) {
      return 1;
    }

    return 0.83;
  });
}

export default function PhotoAlbumBulletsV2({
  containerClassName,
  currentImageIdx,
  imageCount,
  maxBulletsPadding,
  mediaTypes
}) {
  const dots = getBulletScales({
    currentIndex: currentImageIdx,
    maxIndex: imageCount - 1,
    maxBulletsPadding
  });

  return (
    <div className="PhotoAlbumBulletsV2">
      <div
        className={classnames(
          'flex justify-center align-items-center gap-2',
          containerClassName
        )}
      >
        {dots.map((scale, idx) => {
          return (
            <PhotoAlbumBullet
              type={mediaTypes[idx]}
              key={idx}
              mounted={scale > 0}
              additionalStyles={{
                opacity: idx === currentImageIdx ? 1 : 0.7,
                transform: `scale(${scale})`,
                margin: 0,
                height: '10px',
                width: '10px'
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

PhotoAlbumBulletsV2.propTypes = {
  containerClassName: PropTypes.string,
  currentImageIdx: PropTypes.number.isRequired,
  imageCount: PropTypes.number.isRequired,
  maxBulletsPadding: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  mediaTypes: PropTypes.arrayOf(PropTypes.oneOf(['Image', 'Video']))
};

PhotoAlbumBulletsV2.defaultProps = {
  containerClassName: 'pv2',
  maxBulletsPadding: 2,
  mediaTypes: []
};
